import React, { useEffect, useRef, useState } from 'react'
import { parseColumnWidths, parseRowHeights, sanitizeCellText } from './ticketTableFormat'
import { clampRectToTicket, getUseableArea } from '../content/GeneralSettings/GSConstants'

const MIN_COL_WIDTH = 20
const MIN_ROW_HEIGHT = 20

const trimRowNewlines = (cells, rowIndex) =>
  (cells || []).map((row, index) =>
    index !== rowIndex
      ? row
      : (row || []).map((cell) => ({
          ...cell,
          content: String(cell?.content ?? '').replace(/^\n+/, '').replace(/\n+$/, ''),
        }))
  )

const DraggableTable = ({
  id,
  left = 0,
  top = 0,
  rows,
  cols,
  cellHeight,
  cellWidth,
  columnWidths,
  rowHeights,
  cells = [],
  font,
  isSelected,
  items,
  setItems,
  handleDragStart,
  handleDragOver,
  handleItemClick,
  marginInfo,
  containerId = 'containerId',
}) => {
  const [editingCell, setEditingCell] = useState(null)
  const [resizing, setResizing] = useState(null)
  const resizeRef = useRef(null)
  const wrapperRef = useRef(null)

  const widths = parseColumnWidths(columnWidths, cols, cellWidth)
  const heights = parseRowHeights(rowHeights, rows, cellHeight)
  const tableWidth = widths.reduce((sum, width) => sum + width, 0)
  const handlesHidden = Boolean(editingCell)

  const applyGrowToItem = (item, rowIndex, contentHeight) => {
    const currentHeights = parseRowHeights(item.rowHeights, item.rows, item.cellHeight)
    const current = currentHeights[rowIndex] || MIN_ROW_HEIGHT
    const needed = Math.ceil(contentHeight || 0)
    if (needed <= current) return item
    const bounds = getUseableArea(containerId, marginInfo)
    const itemTop = parseFloat(item.top) || 0
    const otherHeight = currentHeights.reduce((sum, height, index) => (index === rowIndex ? sum : sum + height), 0)
    const maxRow = Math.max(current, bounds.bottom - itemTop - otherHeight)
    const nextSize = Math.min(needed, maxRow)
    if (nextSize <= current) return item
    const nextHeights = currentHeights.map((height, index) => (index === rowIndex ? nextSize : height))
    return {
      ...item,
      rowHeights: nextHeights,
      cellHeight: nextHeights[0],
      height: nextHeights.reduce((sum, height) => sum + height, 0),
    }
  }

  const growRowToContent = (rowIndex, contentHeight) => {
    setItems((prevItems) =>
      (prevItems || []).map((item) => (item.id === id ? applyGrowToItem(item, rowIndex, contentHeight) : item))
    )
  }

  const handleTableClick = (e) => {
    e.stopPropagation()
    if (resizing || resizeRef.current) return
    if (handleItemClick) handleItemClick(id)
  }

  const handleCellClick = (e, rowIndex, colIndex) => {
    e.stopPropagation()
    if (resizing || resizeRef.current || editingCell) return
    const nextCells = (cells || []).map((row, r) =>
      row.map((cell, c) => ({
        ...cell,
        isSelected: r === rowIndex && c === colIndex ? !cell.isSelected : cell.isSelected,
      }))
    )
    setItems(
      (items || []).map((item) => {
        if (item.id === id) {
          return { ...item, cells: nextCells, isSelected: false }
        }
        if (item.type === 'table') {
          return {
            ...item,
            isSelected: false,
            cells: (item.cells || []).map((row) =>
              row.map((cell) => ({ ...cell, isSelected: false }))
            ),
          }
        }
        return { ...item, isSelected: false }
      })
    )
  }

  const handleCellDoubleClick = (e, rowIndex, colIndex) => {
    e.stopPropagation()
    const content = cells?.[rowIndex]?.[colIndex]?.content || ''
    const editor = e.currentTarget
    const cellKey = `${rowIndex}-${colIndex}`
    setEditingCell(cellKey)
    requestAnimationFrame(() => {
      editor.textContent = content
      editor.focus()
      const range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(true)
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    })
  }

  const handleCellBlur = (e, rowIndex, colIndex) => {
    const content = sanitizeCellText(e.currentTarget.innerText)
    const contentHeight = e.currentTarget.scrollHeight
    setItems((prevItems) =>
      (prevItems || []).map((item) => {
        if (item.id !== id) return item
        const nextCells = (item.cells || []).map((row, r) =>
          row.map((cell, c) => (r === rowIndex && c === colIndex ? { ...cell, content } : cell))
        )
        return applyGrowToItem({ ...item, cells: nextCells }, rowIndex, contentHeight)
      })
    )
    setEditingCell(null)
  }

  const handleCellInput = (e, rowIndex) => {
    growRowToContent(rowIndex, e.currentTarget.scrollHeight)
  }

  const handleResizeStart = (e, axis, index, edge = 'end') => {
    e.preventDefault()
    e.stopPropagation()
    const startSize =
      axis === 'col'
        ? widths[index] || parseInt(cellWidth, 10) || MIN_COL_WIDTH
        : heights[index] || parseInt(cellHeight, 10) || MIN_ROW_HEIGHT
    const startRenderedHeight =
      wrapperRef.current?.offsetHeight ?? heights.reduce((sum, height) => sum + height, 0)
    const startRenderedWidth =
      wrapperRef.current?.offsetWidth ?? widths.reduce((sum, width) => sum + width, 0)
    resizeRef.current = {
      axis,
      index,
      edge,
      start: axis === 'col' ? e.clientX : e.clientY,
      startSize,
      startTop: top,
      startLeft: left,
      startRenderedHeight,
      startRenderedWidth,
      startBottom: top + startRenderedHeight,
      startRight: left + startRenderedWidth,
      startRow0Size: heights[0] || parseInt(cellHeight, 10) || MIN_ROW_HEIGHT,
      startCol0Size: widths[0] || parseInt(cellWidth, 10) || MIN_COL_WIDTH,
    }
    if (wrapperRef.current) wrapperRef.current.draggable = false
    setResizing(axis)
  }

  const handleStyle = handlesHidden
    ? { visibility: 'hidden', pointerEvents: 'none' }
    : { visibility: 'visible', pointerEvents: 'auto' }

  useEffect(() => {
    if (!resizing) return undefined
    const handleMove = (e) => {
      const state = resizeRef.current
      if (!state) return
      const bounds = getUseableArea(containerId, marginInfo)
      const delta = state.axis === 'col' ? e.clientX - state.start : e.clientY - state.start
      const minSize = state.axis === 'col' ? MIN_COL_WIDTH : MIN_ROW_HEIGHT
      const sizeDelta = state.edge === 'start' ? -delta : delta
      let nextSize = Math.max(minSize, Math.round(state.startSize + sizeDelta))
      setItems((prevItems) =>
        (prevItems || []).map((item) => {
          if (item.id !== id) return item
          if (state.axis === 'col') {
            const currentWidths = parseColumnWidths(item.columnWidths, item.cols, item.cellWidth)
            const otherWidth = currentWidths.reduce((sum, width, index) => (index === state.index ? sum : sum + width), 0)
            if (state.edge === 'start' && state.index === 0) {
              const maxCol0 = state.startRight - bounds.left - otherWidth
              if (maxCol0 >= minSize) {
                nextSize = Math.min(nextSize, maxCol0)
              } else {
                nextSize = Math.min(nextSize, currentWidths[0] || minSize)
              }
              nextSize = Math.max(minSize, nextSize)
              const nextWidths = currentWidths.map((width, index) => (index === state.index ? nextSize : width))
              const newTotalWidth = nextWidths.reduce((sum, width) => sum + width, 0)
              return {
                ...item,
                columnWidths: nextWidths,
                cellWidth: nextWidths[0],
                width: newTotalWidth,
                left: Math.round(state.startRight - newTotalWidth),
              }
            }
            const itemLeft = parseFloat(item.left) || 0
            const maxThisCol = bounds.right - itemLeft - otherWidth
            if (maxThisCol >= minSize) {
              nextSize = Math.min(nextSize, maxThisCol)
            } else {
              nextSize = Math.min(nextSize, currentWidths[state.index] || minSize)
            }
            nextSize = Math.max(minSize, nextSize)
            const nextWidths = currentWidths.map((width, index) => (index === state.index ? nextSize : width))
            const newTotalWidth = nextWidths.reduce((sum, width) => sum + width, 0)
            return {
              ...item,
              columnWidths: nextWidths,
              cellWidth: nextWidths[0],
              width: newTotalWidth,
            }
          }
          const currentHeights = parseRowHeights(item.rowHeights, item.rows, item.cellHeight)
          const otherHeight = currentHeights.reduce((sum, height, index) => (index === state.index ? sum : sum + height), 0)
          if (state.edge === 'start' && state.index === 0) {
            const maxRow0 = state.startBottom - bounds.top - otherHeight
            if (maxRow0 >= minSize) {
              nextSize = Math.min(nextSize, maxRow0)
            } else {
              nextSize = Math.min(nextSize, currentHeights[0] || minSize)
            }
            nextSize = Math.max(minSize, nextSize)
            const nextHeights = currentHeights.map((height, index) => (index === state.index ? nextSize : height))
            const newTotalHeight = nextHeights.reduce((sum, height) => sum + height, 0)
            const didShrink = nextSize < (currentHeights[state.index] || 0)
            return {
              ...item,
              rowHeights: nextHeights,
              cellHeight: nextHeights[0],
              height: newTotalHeight,
              top: Math.round(state.startBottom - newTotalHeight),
              cells: didShrink ? trimRowNewlines(item.cells, state.index) : item.cells,
            }
          }
          const itemTop = parseFloat(item.top) || 0
          const maxThisRow = bounds.bottom - itemTop - otherHeight
          if (maxThisRow >= minSize) {
            nextSize = Math.min(nextSize, maxThisRow)
          } else {
            nextSize = Math.min(nextSize, currentHeights[state.index] || minSize)
          }
          nextSize = Math.max(minSize, nextSize)
          const nextHeights = currentHeights.map((height, index) => (index === state.index ? nextSize : height))
          const newTotalHeight = nextHeights.reduce((sum, height) => sum + height, 0)
          const didShrink = nextSize < (currentHeights[state.index] || 0)
          return {
            ...item,
            rowHeights: nextHeights,
            cellHeight: nextHeights[0],
            height: newTotalHeight,
            cells: didShrink ? trimRowNewlines(item.cells, state.index) : item.cells,
          }
        })
      )
    }
    const handleUp = () => {
      const state = resizeRef.current
      if (state?.edge === 'start' && state.index === 0 && wrapperRef.current) {
        const bounds = getUseableArea(containerId, marginInfo)
        setItems((prevItems) =>
          (prevItems || []).map((item) => {
            if (item.id !== id) return item
            if (state.axis === 'row') {
              const nextTop = Math.round(state.startBottom - wrapperRef.current.offsetHeight)
              return {
                ...item,
                ...clampRectToTicket(item.left, nextTop, wrapperRef.current.offsetWidth, wrapperRef.current.offsetHeight, bounds),
              }
            }
            const nextLeft = Math.round(state.startRight - wrapperRef.current.offsetWidth)
            return {
              ...item,
              ...clampRectToTicket(nextLeft, item.top, wrapperRef.current.offsetWidth, wrapperRef.current.offsetHeight, bounds),
            }
          })
        )
      }
      resizeRef.current = null
      if (wrapperRef.current) wrapperRef.current.draggable = true
      setResizing(null)
    }
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [resizing, id, setItems, marginInfo, containerId])

  const handleDragStartInner = (e) => {
    if (editingCell || resizing || resizeRef.current) {
      e.preventDefault()
      return
    }
    handleDragStart(e)
  }

  const resizeCursor = resizing === 'col' ? 'col-resize' : resizing === 'row' ? 'row-resize' : 'move'

  return (
    <div
      ref={wrapperRef}
      draggable={!editingCell && !resizing}
      className={`item ${isSelected ? 'selectedItem' : ''}`}
      onDragStart={handleDragStartInner}
      onDragOver={(e) => handleDragOver && handleDragOver(e)}
      onDrop={(e) => e.preventDefault()}
      onClick={handleTableClick}
      id={id}
      style={{
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        width: `${tableWidth}px`,
        minWidth: `${tableWidth}px`,
        maxWidth: `${tableWidth}px`,
        cursor: editingCell ? 'text' : resizeCursor,
        zIndex: isSelected ? 2 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '-6px',
          left: '-6px',
          right: '-6px',
          height: '6px',
          cursor: 'pointer',
          zIndex: 5,
        }}
      />
      <span
        style={{
          position: 'absolute',
          bottom: '-6px',
          left: '-6px',
          right: '-6px',
          height: '6px',
          cursor: 'pointer',
          zIndex: 5,
        }}
      />
      <span
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '-6px',
          width: '6px',
          cursor: 'pointer',
          zIndex: 5,
        }}
      />
      <span
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: '-6px',
          width: '6px',
          cursor: 'pointer',
          zIndex: 5,
        }}
      />
      <span
        onMouseDown={(e) => handleResizeStart(e, 'col', 0, 'start')}
        style={{
          position: 'absolute',
          top: '8px',
          left: 0,
          width: '8px',
          bottom: 0,
          cursor: 'col-resize',
          zIndex: 4,
          ...handleStyle,
        }}
      />
      <span
        onMouseDown={(e) => handleResizeStart(e, 'row', 0, 'start')}
        style={{
          position: 'absolute',
          top: 0,
          left: '8px',
          right: 0,
          height: '8px',
          cursor: 'row-resize',
          zIndex: 4,
          ...handleStyle,
        }}
      />
      <table
        style={{
          borderCollapse: 'collapse',
          width: `${tableWidth}px`,
          background: 'white',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          {widths.map((width, index) => (
            <col key={index} style={{ width: `${width}px` }} />
          ))}
        </colgroup>
        <tbody>
          {Array.from({ length: Number(rows) || 0 }).map((_, rowIndex) => {
            const height = heights[rowIndex] || cellHeight
            return (
              <tr key={rowIndex}>
                {Array.from({ length: Number(cols) || 0 }).map((_, colIndex) => {
                  const cell = cells?.[rowIndex]?.[colIndex] || {}
                  const width = widths[colIndex] || cellWidth
                  const cellKey = `${rowIndex}-${colIndex}`
                  const isEditing = editingCell === cellKey
                  return (
                    <td
                      key={colIndex}
                      style={{
                        height: `${height}px`,
                        maxHeight: `${height}px`,
                        width: `${width}px`,
                        minWidth: `${width}px`,
                        maxWidth: `${width}px`,
                        border: cell.isSelected ? '1px solid #0086ff' : '1px solid black',
                        padding: 0,
                        boxSizing: 'border-box',
                        position: 'relative',
                        backgroundColor: cell.isSelected ? 'rgb(242 249 255)' : 'white',
                        verticalAlign: 'top',
                      }}
                      onClick={(e) => handleCellClick(e, rowIndex, colIndex)}
                    >
                      <div
                        style={{
                          height: `${height}px`,
                          maxHeight: `${height}px`,
                          overflow: isEditing ? 'visible' : 'hidden',
                          position: 'relative',
                        }}
                      >
                        <div
                          className="ticket-table-cell-editor"
                          contentEditable={isEditing}
                          suppressContentEditableWarning
                          onDoubleClick={(e) => handleCellDoubleClick(e, rowIndex, colIndex)}
                          onInput={(e) => handleCellInput(e, rowIndex)}
                          onBlur={(e) => handleCellBlur(e, rowIndex, colIndex)}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: isEditing ? 'auto' : 0,
                            minHeight: isEditing ? '100%' : undefined,
                            height: isEditing ? 'auto' : undefined,
                            padding: '4px',
                            boxSizing: 'border-box',
                            fontSize: `${cell.fontSize || font}pt`,
                            textAlign: cell.alignment || 'left',
                            textDecoration: cell.isUnderline ? 'underline' : 'none',
                            fontStyle: cell.isItalic ? 'italic' : 'normal',
                            fontWeight: cell.isBold ? 'bold' : 'normal',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                            overflow: isEditing ? 'visible' : 'hidden',
                            outline: 'none',
                            cursor: isEditing ? 'text' : 'inherit',
                          }}
                        >
                          {isEditing ? null : cell.content}
                        </div>
                      </div>
                      <span
                        onMouseDown={(e) => handleResizeStart(e, 'col', colIndex, 'end')}
                        style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '8px',
                          bottom: 0,
                          cursor: 'col-resize',
                          zIndex: 3,
                          ...handleStyle,
                        }}
                      />
                      <span
                        onMouseDown={(e) => handleResizeStart(e, 'row', rowIndex, 'end')}
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: '8px',
                          bottom: 0,
                          height: '8px',
                          cursor: 'row-resize',
                          zIndex: 3,
                          ...handleStyle,
                        }}
                      />
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default DraggableTable
